import React, { useState, useEffect, useRef } from 'react';
import './BulkUpload.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCloudArrowUp,
  faCheck,
  faExclamationTriangle,
  faRotate
} from '@fortawesome/free-solid-svg-icons';
import {
  DEFAULT_R2_CONTENT_TYPE,
  confirmR2Upload,
  formatBytes,
  formatBytesPerSecond,
  formatDuration,
  putFileToR2,
  requestR2UploadUrl
} from './r2UploadTransport';

const createFileStats = (totalBytes = 0) => ({
  loadedBytes: 0,
  totalBytes,
  speedBytesPerSecond: 0,
  etaSeconds: null
});

const createItemStats = (files, isFastq) => {
  const read1 = createFileStats(files.read1?.size || 0);
  const read2 = createFileStats(isFastq ? files.read2?.size || 0 : 0);
  return {
    read1,
    ...(isFastq ? { read2 } : {}),
    overall: createFileStats((read1.totalBytes || 0) + (read2.totalBytes || 0))
  };
};

const resolveUploadUrl = (descriptor) => descriptor.uploadUrl || descriptor.upload_url;

const resolveR2Headers = (descriptor) => {
  const headers = { ...(descriptor.headers || {}) };
  if (!Object.keys(headers).some(key => key.toLowerCase() === 'content-type')) {
    headers['Content-Type'] = DEFAULT_R2_CONTENT_TYPE;
  }
  return headers;
};

function BulkUpload({ onUploadStatusChange }) {
  const [analyses, setAnalyses] = useState({});
  const [sampleTypes, setSampleTypes] = useState({});
  const [selectedAnalysis, setSelectedAnalysis] = useState('');
  const [selectedSample, setSelectedSample] = useState('');
  const [parentProjects, setParentProjects] = useState([]);
  const [subprojects, setSubprojects] = useState({});
  const [patients, setPatients] = useState([]);

  const [selectedParentId, setSelectedParentId] = useState('');
  const [selectedSubprojectId, setSelectedSubprojectId] = useState('');

  const [uploadQueue, setUploadQueue] = useState([]);
  const [uploadProgress, setUploadProgress] = useState({});
  const [uploadStats, setUploadStats] = useState({});
  const [errors, setErrors] = useState([]);
  const [activeUploads, setActiveUploads] = useState(0);
  
  // Error notification system with IDs and timeouts
  const [visibleErrors, setVisibleErrors] = useState([]);
  const errorTimeoutsRef = useRef({});
  
  // Clear all existing timeouts
  const clearErrorTimeouts = () => {
    Object.values(errorTimeoutsRef.current).forEach(timeout => {
      clearTimeout(timeout);
    });
    errorTimeoutsRef.current = {};
  };

  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef(null);

  const MAX_CONCURRENT_UPLOADS = 2;

  const activeControllersRef = useRef({});
  const uploadMetricsRef = useRef({});

  // 1) Fetch the supported analyses from the new backend
  useEffect(() => {
    fetch('https://test-tunnel-b1d9.szapfs.org/supported-analyses')
      .then(res => res.json())
      .then(data => {
        setAnalyses(data.analyses || {});
        setSampleTypes(data.sampleTypes || {});
      })
      .catch(err => console.error('Failed to fetch analyses:', err));
  }, []);

  // 2) Fetch parent projects from service9
  useEffect(() => {
    fetch('https://service9.szapfs.org/parent-projects')
      .then(res => res.json())
      .then(data => {
        const sorted = data.sort((a, b) => a.id - b.id);
        setParentProjects(sorted);
      })
      .catch(err => console.error('Failed to fetch parent projects:', err));
  }, []);

  // 3) If parent changes, fetch subprojects from service9
  useEffect(() => {
    if (selectedParentId) {
      fetch(`https://service9.szapfs.org/parent-project/${selectedParentId}/subprojects`)
        .then(res => res.json())
        .then(data => {
          setSubprojects(prev => ({ ...prev, [selectedParentId]: data }));
        })
        .catch(err => console.error('Failed to fetch subprojects:', err));
    }
  }, [selectedParentId]);

  // 4) If both parent and subproject are selected, fetch patients
  useEffect(() => {
    if (selectedParentId && selectedSubprojectId) {
      fetch(`https://service9.szapfs.org/project/${selectedParentId}/${selectedSubprojectId}/patients`)
        .then(res => res.json())
        .then(data => {
          const sorted = data.sort((a, b) => {
            const aNum = parseInt(a.patient_id.split('_')[2]) || 0;
            const bNum = parseInt(b.patient_id.split('_')[2]) || 0;
            return aNum - bNum;
          });
          setPatients(sorted);
        })
        .catch(err => console.error('Failed to fetch patients:', err));
    }
  }, [selectedParentId, selectedSubprojectId]);

  // Concurrency check
  useEffect(() => {
    if (uploadQueue.length > 0 && activeUploads < MAX_CONCURRENT_UPLOADS) {
      const nextItem = uploadQueue.find(
        it => !it.isUploading && !it.isComplete && !it.hasError && !it.isCanceled
      );
      if (nextItem) startUpload(nextItem);
    }
  }, [uploadQueue, activeUploads]);

  // Handle error display and automatic removal
  useEffect(() => {
    if (errors.length > 0) {
      // Clear all existing errors immediately when new errors arrive
      clearErrorTimeouts();
      setVisibleErrors([]);
      
      // Generate unique IDs for each error
      const errorsWithIds = errors.map(error => ({
        id: Date.now() + '-' + Math.random().toString(36).substr(2, 5),
        message: error,
        timestamp: Date.now()
      }));
      
      // Display each error immediately
      setVisibleErrors(errorsWithIds);
      
      // Set up removal timeouts for each error individually
      errorsWithIds.forEach(error => {
        // Each error has its own 3-second timeout
        const timeoutId = setTimeout(() => {
          setVisibleErrors(prev => prev.filter(e => e.id !== error.id));
          // Remove the timeout reference
          delete errorTimeoutsRef.current[error.id];
        }, 3000);
        
        // Store the timeout ID
        errorTimeoutsRef.current[error.id] = timeoutId;
      });
      
      // Clear the original errors array
      setErrors([]);
    }
  }, [errors]);

  const isUploading = uploadQueue.some(
    item => item.isUploading || (!item.isComplete && !item.hasError && !item.isCanceled)
  );

  // Update parent component about upload status
  useEffect(() => {
    if (onUploadStatusChange) {
      onUploadStatusChange(isUploading);
    }
  }, [isUploading, onUploadStatusChange]);

  const analysisDetails = analyses[selectedAnalysis] || {};
  const isFastq = analysisDetails.uploadType === 'FASTQ';
  const allowedSamples = analysisDetails.supportedSamples || [];
  const isDropzoneEnabled = selectedAnalysis && selectedSample && selectedParentId && selectedSubprojectId;

  const updateQueueItem = (patientId, patch) => {
    setUploadQueue(prev =>
      prev.map(q => (q.patientId === patientId ? { ...q, ...patch } : q))
    );
  };

  const updateR2Progress = (patientId, role, progress) => {
    if (!uploadMetricsRef.current[patientId]) {
      uploadMetricsRef.current[patientId] = {
        loadedBytes: {},
        totalBytes: {},
        speedBytesPerSecond: {}
      };
    }

    uploadMetricsRef.current[patientId].loadedBytes[role] = progress.loadedBytes;
    uploadMetricsRef.current[patientId].totalBytes[role] = progress.totalBytes;
    uploadMetricsRef.current[patientId].speedBytesPerSecond[role] = progress.speedBytesPerSecond;

    const metrics = uploadMetricsRef.current[patientId];
    const totalBytes = Object.values(metrics.totalBytes).reduce((sum, value) => sum + (value || 0), 0);
    const loadedBytes = Object.values(metrics.loadedBytes).reduce((sum, value) => sum + (value || 0), 0);
    const speedBytesPerSecond = Object.values(metrics.speedBytesPerSecond).reduce((sum, value) => sum + (value || 0), 0);
    const etaSeconds = speedBytesPerSecond > 0 && totalBytes > loadedBytes
      ? (totalBytes - loadedBytes) / speedBytesPerSecond
      : null;

    setUploadProgress(prev => ({
      ...prev,
      [patientId]: {
        ...(prev[patientId] || {}),
        [role]: progress.percent,
        overall: totalBytes > 0 ? (loadedBytes / totalBytes) * 100 : progress.percent
      }
    }));

    setUploadStats(prev => ({
      ...prev,
      [patientId]: {
        ...(prev[patientId] || {}),
        [role]: progress,
        overall: {
          loadedBytes,
          totalBytes,
          speedBytesPerSecond,
          etaSeconds
        }
      }
    }));
  };

  const requestR2File = async ({ item, file, fileRole, uploadId, controller }) => {
    updateQueueItem(item.patientId, { stage: `Requesting ${fileRole === 'single' ? 'file' : fileRole} upload URL` });

    const descriptor = await requestR2UploadUrl({
      ...(uploadId ? { uploadId } : {}),
      patientId: item.patient.patient_id,
      subprojectId: selectedSubprojectId,
      analysisCode: selectedAnalysis,
      sampleCode: selectedSample,
      fileRole,
      filename: file.name,
      contentType: DEFAULT_R2_CONTENT_TYPE,
      sizeBytes: file.size
    }, controller.signal);

    return {
      ...descriptor,
      uploadUrl: resolveUploadUrl(descriptor),
      fileRole,
      filename: file.name,
      sizeBytes: file.size
    };
  };

  const uploadPreparedR2File = async ({ item, file, descriptor, progressRole, controller }) => {
    updateQueueItem(item.patientId, { stage: `Uploading ${file.name}` });

    const result = await putFileToR2({
      uploadUrl: descriptor.uploadUrl,
      file,
      headers: resolveR2Headers(descriptor),
      signal: controller.signal,
      onProgress: progress => updateR2Progress(item.patientId, progressRole, progress)
    });

    return {
      fileRole: descriptor.fileRole,
      filename: file.name,
      r2Key: descriptor.r2Key,
      sizeBytes: file.size,
      ...(result.etag ? { etag: result.etag } : {})
    };
  };

  // Start the entire upload for one item
  const startUpload = async (item) => {
    setActiveUploads(prev => prev + 1);

    const controller = new AbortController();
    activeControllersRef.current[item.patientId] = controller;
    item.controller = controller;
    updateQueueItem(item.patientId, { isUploading: true, stage: 'Preparing R2 upload', controller });

    const metadata = {
      analysisCode: selectedAnalysis,
      sampleCode: selectedSample,
      patientId: item.patient.patient_id,
      subprojectId: selectedSubprojectId,
      originalFileName: item.files.read1.name + (isFastq && item.files.read2 ? ',' + item.files.read2.name : '')
    };

    try {
      let logicalUploadId = null;
      let uploadedR2Files = [];

      if (isFastq) {
        const read1Descriptor = await requestR2File({
          item,
          file: item.files.read1,
          fileRole: 'read1',
          controller
        });
        logicalUploadId = read1Descriptor.uploadId;

        const read2Descriptor = await requestR2File({
          item,
          file: item.files.read2,
          fileRole: 'read2',
          uploadId: logicalUploadId,
          controller
        });

        uploadedR2Files = await Promise.all([
          uploadPreparedR2File({
            item,
            file: item.files.read1,
            descriptor: read1Descriptor,
            progressRole: 'read1',
            controller
          }),
          uploadPreparedR2File({
            item,
            file: item.files.read2,
            descriptor: read2Descriptor,
            progressRole: 'read2',
            controller
          })
        ]);
      } else {
        const descriptor = await requestR2File({
          item,
          file: item.files.read1,
          fileRole: 'single',
          controller
        });
        logicalUploadId = descriptor.uploadId;
        uploadedR2Files = [await uploadPreparedR2File({
          item,
          file: item.files.read1,
          descriptor,
          progressRole: 'read1',
          controller
        })];
      }

      updateQueueItem(item.patientId, { stage: 'Confirming R2 upload' });

      await confirmR2Upload({
        uploadId: logicalUploadId,
        metadata,
        files: uploadedR2Files.map(({ fileRole, filename, r2Key, sizeBytes, etag }) => ({
          fileRole,
          filename,
          r2Key,
          sizeBytes,
          ...(etag ? { etag } : {})
        }))
      }, controller.signal);

      setUploadQueue(prev =>
        prev.map(q => (
          q.patientId === item.patientId
            ? { ...q, isComplete: true, isUploading: false, stage: 'Accepted by backend' }
            : q
        ))
      );
    } catch (err) {
      if (err.name === 'AbortError') {
        console.log(`Upload canceled for patient: ${item.patientId}`);
      } else {
        console.error('Upload error:', err);
        setUploadQueue(prev =>
          prev.map(q =>
            q.patientId === item.patientId
              ? { ...q, hasError: true, isUploading: false, error: err.message, stage: 'Upload failed' }
              : q
          )
        );
      }
    } finally {
      delete activeControllersRef.current[item.patientId];
      item.controller = null;
      setActiveUploads(prev => prev - 1);
    }
  };

  const cancelUpload = (item) => {
    item.isCanceled = true;
    const controller = activeControllersRef.current[item.patientId] || item.controller;
    if (controller) {
      controller.abort();
    }
    delete activeControllersRef.current[item.patientId];
    delete uploadMetricsRef.current[item.patientId];
    setUploadQueue(prev => prev.filter(q => q.patientId !== item.patientId));
    setUploadProgress(prev => {
      const next = { ...prev };
      delete next[item.patientId];
      return next;
    });
    setUploadStats(prev => {
      const next = { ...prev };
      delete next[item.patientId];
      return next;
    });
  };

  // Cleanup function to cancel all uploads when component unmounts
  useEffect(() => {
    return () => {
      // Abort all active uploads when component unmounts
      Object.values(activeControllersRef.current).forEach(controller => {
        if (controller && typeof controller.abort === 'function') {
          controller.abort();
        }
      });
      
      console.log('BulkUpload component unmounted, all uploads aborted');
    };
  }, []);

  // Clear queue when selections change
  const handleSelectionChange = () => {
    if (uploadQueue.length > 0) {
      // Cancel all ongoing uploads
      uploadQueue.forEach(item => {
        const controller = activeControllersRef.current[item.patientId] || item.controller;
        if (controller && !item.isComplete && !item.isCanceled) {
          controller.abort();
        }
      });
      
      // Clear the queue
      setUploadQueue([]);
      setUploadProgress({});
      setUploadStats({});
      uploadMetricsRef.current = {};
      activeControllersRef.current = {};
      setActiveUploads(0);
      
    }
  };

  // Handle analysis selection change
  const handleAnalysisChange = (e) => {
    const newAnalysis = e.target.value;
    setSelectedAnalysis(newAnalysis);
    setSelectedSample('');
    handleSelectionChange();
  };

  // Handle sample type selection change
  const handleSampleChange = (e) => {
    setSelectedSample(e.target.value);
    handleSelectionChange();
  };

  // Handle parent project selection change
  const handleParentProjectChange = (e) => {
    setSelectedParentId(e.target.value);
    setSelectedSubprojectId('');
    handleSelectionChange();
  };

  // Handle subproject selection change
  const handleSubprojectChange = (e) => {
    setSelectedSubprojectId(e.target.value);
    handleSelectionChange();
  };

  const validateFiles = (files) => {
    const newErrors = [];
    const grouped = {};
    const finalQueue = [];
    const validPatientIds = patients.map(p => p.patient_id);
    
    // Create a map of existing file names in the queue for quick lookup
    const existingFileNames = new Set(uploadQueue.map(item => 
      isFastq 
        ? [item.files.read1?.name, item.files.read2?.name].filter(Boolean)
        : [item.files.read1?.name].filter(Boolean)
    ).flat());

    // For non-FASTQ, track patient IDs that are already in the queue
    const existingPatientIds = new Set(
      !isFastq ? uploadQueue.map(item => item.patientId) : []
    );

    for (let file of files) {
      // Skip duplicates (file names)
      if (existingFileNames.has(file.name)) {
        newErrors.push(`Duplicate file "${file.name}" already in queue`);
        continue;
      }
      
      const baseName = file.name.replace(/\.[^/.]+$/, '');
      const patientNumber = baseName.split('_')[0];
      const paddedParent = String(selectedParentId).padStart(2, '0');
      const fullPatientId = `${paddedParent}_${selectedSubprojectId}_${patientNumber}`;

      if (!validPatientIds.includes(fullPatientId)) {
        newErrors.push(`Invalid file "${file.name}": patient "${patientNumber}" not found`);
        continue;
      }
      
      // For non-FASTQ, check for duplicate patient IDs
      if (!isFastq && existingPatientIds.has(fullPatientId)) {
        newErrors.push(`Patient "${patientNumber}" already has a file in the queue`);
        continue;
      }
      
      if (!grouped[fullPatientId]) {
        grouped[fullPatientId] = { files: {} };
      }
      const isR1 = file.name.includes('R1');
      const isR2 = file.name.includes('R2');
      if (isFastq) {
        if (!isR1 && !isR2) {
          newErrors.push(`File "${file.name}" must have R1 or R2 in name for FASTQ`);
          continue;
        }
        if (isR1) grouped[fullPatientId].files.read1 = file;
        if (isR2) grouped[fullPatientId].files.read2 = file;
      } else {
        if (!grouped[fullPatientId].files.read1) {
          grouped[fullPatientId].files.read1 = file;
          existingPatientIds.add(fullPatientId); // Track this patient ID
        } else {
          newErrors.push(`Extra file "${file.name}" for single-file analysis (patient "${patientNumber}")`);
        }
      }
    }

    // build finalQueue
    for (const pid of Object.keys(grouped)) {
      const pObj = patients.find(p => p.patient_id === pid);
      if (!pObj) continue;
      const { read1, read2 } = grouped[pid].files;
      if (isFastq) {
        if (!read1 || !read2) {
          newErrors.push(`Missing R1 or R2 for patient "${pid.split('_')[2]}"`);
          continue;
        }
        finalQueue.push({ patientId: pid, patient: pObj, files: { read1, read2 } });
      } else {
        if (read1) {
          finalQueue.push({ patientId: pid, patient: pObj, files: { read1 } });
        }
      }
    }

    setErrors(newErrors);

    const queueItems = finalQueue.map(it => ({
      ...it,
      isUploading: false,
      isComplete: false,
      hasError: false,
      isCanceled: false,
      error: null,
      stage: 'Waiting'
    }));

    setUploadQueue(prev => [...prev, ...queueItems]);

    const newProgress = {};
    const newStats = {};
    queueItems.forEach(i => {
      newProgress[i.patientId] = {
        read1: 0,
        ...(isFastq ? { read2: 0 } : {}),
        overall: 0
      };
      newStats[i.patientId] = createItemStats(i.files, isFastq);
      uploadMetricsRef.current[i.patientId] = {
        loadedBytes: { read1: 0, read2: 0 },
        totalBytes: {
          read1: i.files.read1?.size || 0,
          read2: isFastq ? i.files.read2?.size || 0 : 0
        },
        speedBytesPerSecond: { read1: 0, read2: 0 }
      };
    });
    setUploadProgress(prev => ({ ...prev, ...newProgress }));
    setUploadStats(prev => ({ ...prev, ...newStats }));
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    if (isDropzoneEnabled) {
      e.dataTransfer.dropEffect = 'copy';
      setIsDragOver(true);
    }
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    if (!isDropzoneEnabled) return;
    const droppedFiles = e.dataTransfer?.files || e.target.files;
    if (droppedFiles?.length) {
      validateFiles(droppedFiles);
    }
    setIsDragOver(false);
  };

  return (
    <div className={`bulk-upload-container ${isUploading ? 'uploading-in-progress' : ''}`}>
      <div className="bulk-upload-grid">
          <div className="bulk-dropdown">
            <select
              value={selectedAnalysis}
              onChange={handleAnalysisChange}
            >
              <option value="">Analysis Type</option>
              {Object.entries(analyses).map(([code, details]) => (
                <option key={code} value={code}>
                  {details.name} ({code})
                </option>
              ))}
            </select>
          </div>
          <div className="bulk-dropdown">
            <select
              value={selectedSample}
              onChange={handleSampleChange}
              disabled={!selectedAnalysis}
            >
              <option value="">Sample Type</option>
              {Object.entries(sampleTypes)
                .filter(([smCode]) => allowedSamples.includes(smCode))
                .map(([smCode, sm]) => (
                  <option key={smCode} value={smCode}>
                    {sm.name} ({smCode})
                  </option>
                ))}
            </select>
          </div>
          <div className="bulk-dropdown">
            <select
              value={selectedParentId}
              onChange={handleParentProjectChange}
            >
              <option value="">Project</option>
              {parentProjects.map(pp => (
                <option key={pp.id} value={pp.id}>
                  {pp.name}
                </option>
              ))}
            </select>
          </div>
          <div className="bulk-dropdown">
            <select
              value={selectedSubprojectId}
              onChange={handleSubprojectChange}
              disabled={!selectedParentId}
            >
              <option value="">Subproject</option>
              {(subprojects[selectedParentId] || []).map(sp => (
                <option key={sp.project_id} value={sp.project_id}>
                  {sp.project_id} - ({sp.description})
                </option>
              ))}
            </select>
          </div>
    
      </div>

      <div 
        className={`drop-zone ${!isDropzoneEnabled ? 'disabled' : ''} ${isDragOver ? 'drag-over' : ''}`}
        onDragOver={isDropzoneEnabled ? handleDragOver : null}
        onDragLeave={isDropzoneEnabled ? handleDragLeave : null}
        onDrop={isDropzoneEnabled ? handleDrop : null}
        onClick={isDropzoneEnabled ? () => fileInputRef.current?.click() : null}
      >
        <input
          type="file"
          ref={fileInputRef}
          style={{ display: 'none' }}
          onChange={(e) => handleDrop(e)}
          multiple
          disabled={!isDropzoneEnabled}
        />
        <FontAwesomeIcon icon={faCloudArrowUp} className="upload-icon" />
        <div className="drop-zone-text">
          {isDropzoneEnabled ? (
            <>
              <p>Drag &amp; drop your files here, or click to select files</p>
              <p className="drop-zone-subtext">
                {isFastq 
                  ? "For FASTQ, ensure each patient has both R1 and R2 files" 
                  : "Each patient should have a single file"}
              </p>
            </>
          ) : (
            <>
              <p>Please complete all selections first</p>
              <p className="drop-zone-subtext">
                Select Analysis Type, Sample Type, Project and Subproject to enable file upload
              </p>
            </>
          )}
          {errors.length > 0 && (
            <div className="upload-errors">
              <h4>Upload Issues:</h4>
              <ul>
                {errors.map((error, index) => (
                  <li key={index}>{error}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
      <div className="upload-grid-container">

        <div className="upload-grid">
          {[...uploadQueue].sort((a, b) => {
            // Active uploads first
            if (!a.isComplete && !a.isCanceled && !a.hasError && a.isUploading) return -1;
            if (!b.isComplete && !b.isCanceled && !b.hasError && b.isUploading) return 1;
            
            // Pending uploads next
            if (!a.isComplete && !a.isCanceled && !a.hasError) return -1;
            if (!b.isComplete && !b.isCanceled && !b.hasError) return 1;
            
            // Completed uploads last
            return 0;
          }).map((item) => {
            const progress = uploadProgress[item.patientId] || {};
            const stats = uploadStats[item.patientId] || createItemStats(item.files, isFastq);
            const overallStats = stats.overall || createFileStats();
            const read1Stats = stats.read1 || createFileStats(item.files.read1?.size || 0);
            const read2Stats = stats.read2 || createFileStats(item.files.read2?.size || 0);
            return (
              <div 
                key={item.patientId} 
                className={`upload-item
                  ${item.isComplete ? 'complete' : ''}
                  ${item.hasError ? 'error' : ''}
                  ${item.isCanceled ? 'canceled' : ''}
                  ${(!item.isComplete && !item.isCanceled && !item.hasError && item.isUploading) ? 'active-upload' : ''}
                `}
                style={{ 
                  opacity: (!item.isComplete && !item.isCanceled && !item.hasError && item.isUploading) ? 1 : 0.7
                }}
              >
                {(!item.isComplete && !item.isCanceled && !item.hasError && item.isUploading) && (
                  <div className="uploading-indicator">
                    <FontAwesomeIcon icon={faRotate} spin />
                    <span>Uploading</span>
                  </div>
                )}
                <div className="patient-info">
                  <span className="patient-name">{item.patient.name}</span>
                  <span className="patient-id">{item.patient.patient_id}</span>
                </div>
                <div className="bulk-upload-stage">{item.stage}</div>
                <div className="bulk-upload-metrics">
                  <span>{formatBytes(overallStats.loadedBytes)} / {formatBytes(overallStats.totalBytes)}</span>
                  <span>{formatBytesPerSecond(overallStats.speedBytesPerSecond)}</span>
                  <span>ETA {formatDuration(overallStats.etaSeconds)}</span>
                </div>
                <div className="file-progress overall-progress">
                  <span>Overall: {Math.round(progress.overall || 0)}%</span>
                  <div className="progress-bar">
                    <div
                      className="progress"
                      style={{ width: `${progress.overall || 0}%` }}
                    />
                  </div>
                </div>
                <div className="file-info">
                  <div className="file-progress">
                    <span>
                      {isFastq ? 'R1' : 'File'}: {item.files.read1.name}
                      <small>{Math.round(progress.read1 || 0)}% | {formatBytesPerSecond(read1Stats.speedBytesPerSecond)}</small>
                    </span>
                    <div className="progress-bar">
                      <div
                        className="progress"
                        style={{ width: `${progress.read1 || 0}%` }}
                      />
                    </div>
                  </div>
                  {isFastq && item.files.read2 && (
                    <div className="file-progress">
                      <span>
                        R2: {item.files.read2.name}
                        <small>{Math.round(progress.read2 || 0)}% | {formatBytesPerSecond(read2Stats.speedBytesPerSecond)}</small>
                      </span>
                      <div className="progress-bar">
                        <div
                          className="progress"
                          style={{ width: `${progress.read2 || 0}%` }}
                        />
                      </div>
                    </div>
                  )}
                </div>
                {item.hasError && (
                  <div className="error-message">
                    <FontAwesomeIcon icon={faExclamationTriangle} /> {item.error}
                  </div>
                )}
                {item.isComplete && !item.hasError && (
                  <div className="complete-icon">
                    <FontAwesomeIcon icon={faCheck} />
                  </div>
                )}
                {!item.isComplete && !item.isCanceled && !item.hasError && (
                  <button className="cancel-upload-btn" onClick={() => cancelUpload(item)}>
                    Cancel
                  </button>
                )}
              </div>
            );
          })}
        </div>
        </div>
        <div className="bulk-upload-information">{uploadQueue.filter(item => item.isComplete).length}/{uploadQueue.length} files uploaded</div>

        <div className="errorbulk-container">
          {visibleErrors.map((error) => (
            <div key={error.id} className="errorbulk-notification">
              <FontAwesomeIcon icon={faExclamationTriangle} />
              <span>{error.message}</span>
            </div>
          ))}
        </div>
    </div>
  );
}

export default BulkUpload;
